import React from 'react'
import { useApp } from '../../context/AppContext'
import { ValidationError, VersionMismatchError, api } from '../../api/client'
import type { TaxRegion } from '../../api/client'
import Panel, { PanelStatus } from './Panel'
import Field, { useFocusFirstInvalid } from './Field'
import { BusinessForm, meWithBusiness, toBusinessForm, toBusinessPatch } from './businessForm'
import { saveErrorMessage, showToast } from './toast'

const NOTE_MAX = 500

const TAX_REGIONS: { value: TaxRegion; label: string }[] = [
  { value: 'scotland', label: 'Scotland' },
  { value: 'rest_of_uk', label: 'Rest of the UK' },
]

const FIELDS: {
  key: keyof BusinessForm
  label: string
  hint?: string
  autoComplete?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
}[] = [
  { key: 'trading_name', label: 'Business name', autoComplete: 'organization' },
  { key: 'address_line1', label: 'Address', autoComplete: 'address-line1' },
  { key: 'address_line2', label: 'Address line 2 (optional)', autoComplete: 'address-line2' },
  { key: 'city', label: 'Town or city', autoComplete: 'address-level2' },
  { key: 'postcode', label: 'Postcode (optional)', autoComplete: 'postal-code' },
  { key: 'phone', label: 'Phone', autoComplete: 'tel', inputMode: 'tel', hint: 'Clients see this on confirmations.' },
]

export default function BusinessPanel() {
  const { state, dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [form, setForm] = React.useState<BusinessForm>(() => toBusinessForm(null))
  const [savedForm, setSavedForm] = React.useState<BusinessForm>(() => toBusinessForm(null))
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState('')
  // The version the form was filled from (If-Match), and whether the last
  // save lost to another device.
  const [version, setVersion] = React.useState<number | undefined>(undefined)
  const [stale, setStale] = React.useState(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  useFocusFirstInvalid(errors, formRef)

  const load = React.useCallback(async () => {
    setStatus('loading')
    setStale(false)
    setSaveError('')
    try {
      const business = await api.getBusiness()
      setForm(toBusinessForm(business))
      setSavedForm(toBusinessForm(business))
      setVersion(business?.version)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaveError('')
    setStale(false)
    setSaving(true)
    setErrors({})
    try {
      const saved = await api.updateBusiness(toBusinessPatch(form), version)
      setForm(toBusinessForm(saved))
      setSavedForm(toBusinessForm(saved))
      setVersion(saved.version)
      if (state.me) {
        dispatch({ type: 'SESSION_STARTED', payload: meWithBusiness(state.me, saved) })
      }
      showToast(dispatch, 'Business details saved')
    } catch (error) {
      if (error instanceof ValidationError) {
        setErrors(error.fields)
      } else {
        setStale(error instanceof VersionMismatchError)
        setSaveError(saveErrorMessage(error))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Panel
      title="Business details"
      unsavedChanges={status === 'ready' && JSON.stringify(form) !== JSON.stringify(savedForm)}
      intro="Your name, address and phone, as clients see them.">
      {status !== 'ready' ? (
        <PanelStatus status={status} onRetry={load} />
      ) : (
        <form ref={formRef} className="settings-form" onSubmit={save} noValidate>
          {FIELDS.map((field) => (
            <Field key={field.key} id={`business-${field.key}`} label={field.label} hint={field.hint} error={errors[field.key]}>
              <input
                id={`business-${field.key}`}
                value={form[field.key]}
                autoComplete={field.autoComplete}
                inputMode={field.inputMode}
                aria-invalid={Boolean(errors[field.key])}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
              />
            </Field>
          ))}
          <Field id="business-tax-region" label="Tax region" hint="Clients don’t see this. It’s for your tax estimate in Earnings." error={errors.tax_region}>
            <div className="chip-group" id="business-tax-region" role="group" aria-label="Tax region">
              {TAX_REGIONS.map((region) => (
                <button
                  key={region.value}
                  type="button"
                  className="chip"
                  aria-pressed={form.tax_region === region.value}
                  onClick={() => setForm({ ...form, tax_region: region.value })}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </Field>
          <Field
            id="business-confirmation-note"
            label="Note on confirmation emails (optional)"
            hint={`Clients see this under their appointment details. ${form.confirmation_note.length}/${NOTE_MAX}`}
            error={errors.confirmation_note}
          >
            <textarea
              id="business-confirmation-note"
              rows={4}
              maxLength={NOTE_MAX}
              value={form.confirmation_note}
              placeholder="Parking, directions or what to bring"
              onChange={(e) => setForm({ ...form, confirmation_note: e.target.value })}
            />
          </Field>
          {saveError && (
            <div className="field-error" role="alert">
              <p>{saveError}</p>
              {stale && (
                <button type="button" className="btn btn-secondary btn-compact" onClick={load}>
                  Reload
                </button>
              )}
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}
    </Panel>
  )
}
